import type {CommentingImplementation, Thread} from '@github-ui/conversations'
import type {DiffLine} from '@github-ui/diff-lines'
import {noop} from '@github-ui/noop'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useCallback, useMemo} from 'react'
import {readInlineData, useRelayEnvironment} from 'react-relay'

import addCommitCommentMutation from '../mutations/add-commit-comment'
import type {CommitPayload} from '../types/commit-types'
import type {useFetchCommitThreadCommitComment$key} from './__generated__/useFetchCommitThreadCommitComment.graphql'
import {useCommentInfo} from './use-comment-info'
import {CommitCommentFragment, useFetchThread} from './use-fetch-commit-thread'

export function useDiffInlineCommenting(): CommentingImplementation {
  const payload = useRoutePayload<CommitPayload>()
  const {commit} = payload
  const environment = useRelayEnvironment()
  const commentInfo = useCommentInfo()
  const fetchThread = useFetchThread(commentInfo.canComment)

  const addThreadReply = useCallback(
    ({
      commentsConnectionIds = [],
      filePath,
      onCompleted,
      onError,
      text,
      thread,
      threadsConnectionId,
    }: {
      commentsConnectionIds?: string[]
      filePath: string
      onCompleted?: (commentDatabaseId?: number) => void
      onError: (error: Error) => void
      text: string
      thread: Thread
      threadsConnectionId?: string
    }) => {
      addCommitCommentMutation({
        commentsConnectionIds,
        environment,
        input: {
          commitId: commit.globalRelayId,
          body: text,
          path: filePath,
          // @ts-expect-error TODO: Fix this, it's being passed back but the type doesn't expect it
          position: thread.position,
        },
        onCompleted: response => {
          const commentNode = response.addCommitComment?.comment
          let commentId: number | undefined | null
          if (commentNode) {
            const commentData = readInlineData<useFetchCommitThreadCommitComment$key>(
              CommitCommentFragment,
              commentNode,
            )
            commentId = commentData.databaseId
          }

          // TODO - refresh diff lines to get updated comment count on marker
          onCompleted?.(commentId ?? undefined)
        },
        onError,
        threadsConnectionId,
      })
    },
    [commit.globalRelayId, environment],
  )

  const addThread = useCallback(
    ({
      text,
      diffLine,
      filePath,
      onCompleted,
      onError,
      threadsConnectionId,
    }: {
      text: string
      diffLine?: DiffLine
      filePath: string
      onCompleted?: (threadId: string, commentDatabaseId?: number) => void
      onError?: (error: Error) => void
      threadsConnectionId?: string
    }) => {
      // commit comments require position but PRs does not
      // so we pass it into our build_file_diff_payload
      if (!diffLine) return
      if (!diffLine.position) return

      addCommitCommentMutation({
        environment,
        commentsConnectionIds: threadsConnectionId ? [threadsConnectionId] : [],
        input: {
          body: text,
          position: diffLine.position,
          path: filePath,
          commitId: commit.globalRelayId,
        },
        onCompleted: response => {
          const commentNode = response.addCommitComment?.comment
          const threadId = response.addCommitComment?.thread?.id
          let commentId: number | undefined | null
          if (commentNode) {
            const commentData = readInlineData<useFetchCommitThreadCommitComment$key>(
              CommitCommentFragment,
              commentNode,
            )
            commentId = commentData.databaseId
          }

          // TODO - refresh diff lines to get new thread marker
          onCompleted?.(threadId ?? '', commentId ?? undefined)
        },
        onError: () => {
          onError?.(new Error())
        },
      })
    },
    [commit.globalRelayId, environment],
  )

  return useMemo(
    () => ({
      batchingEnabled: false,
      commentSubjectType: 'commit',
      multilineEnabled: false,
      resolvingEnabled: false,
      suggestedChangesEnabled: false,
      addThread,
      addThreadReply,
      deleteComment: noop,
      editComment: noop,
      pendingSuggestedChangesBatch: [],
      submitSuggestedChanges: noop,
      addSuggestedChangeToPendingBatch: noop,
      removeSuggestedChangeFromPendingBatch: noop,
      addFileLevelThread: noop,
      resolveThread: noop,
      unresolveThread: noop,
      fetchThread,
      commentBoxConfig: {
        pasteUrlsAsPlainText: false,
        useMonospaceFont: false,
      },
    }),
    [addThread, addThreadReply, fetchThread],
  )
}
